import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Badge } from '@/components/ui/Badge'
import { useToast } from '@/components/ui/Toast'
import { Button } from '@/components/ui/Button'
import { Card, CardBody } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { Input } from '@/components/ui/Input'
import { Sheet } from '@/components/ui/Sheet'
import { SkeletonRows } from '@/components/ui/Skeleton'
import { canWriteDepartment } from '@/auth/profile'
import { useProfile } from '@/auth/SessionProvider'
import { addMemberToDepartment, listDepartmentMembers } from '@/data/members'
import type { AddMemberResult } from '@/data/members'
import { formatKwacha } from '@/lib/money'
import { formatZambianPhone, normalizeZambianPhone } from '@/lib/phone'
import { lusakaToday } from '@/lib/periods'
import { addMemberSchema } from './schemas'
import type { AddMemberValues } from './schemas'

const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong. Please try again.')

function outcomeMessage(r: AddMemberResult): string {
  switch (r.kind) {
    case 'created':
      return `Added ${r.fullName}.`
    case 'added-existing':
      return `${r.fullName} was already in the church records (another department) and has now been added to yours.`
    case 'already-member':
      return `${r.fullName} is already in your department.`
  }
}

function AddMemberForm({ deptId, onDone }: { deptId: string; onDone: (message: string) => void }) {
  const qc = useQueryClient()
  const busy = useRef(false)
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AddMemberValues>({
    resolver: zodResolver(addMemberSchema),
    defaultValues: { fullName: '', phone: '', email: '', joinedOn: lusakaToday() },
  })

  const add = useMutation({
    mutationFn: (v: AddMemberValues) =>
      addMemberToDepartment({
        departmentId: deptId,
        fullName: v.fullName,
        phone: normalizeZambianPhone(v.phone),
        email: v.email.trim() || null,
        joinedOn: v.joinedOn,
      }),
    onSuccess: async (result) => {
      await qc.invalidateQueries({ queryKey: ['members', deptId] })
      onDone(outcomeMessage(result))
    },
    onSettled: () => {
      busy.current = false
    },
  })

  const submit = handleSubmit((v) => {
    if (busy.current) return
    busy.current = true
    add.mutate(v)
  })

  return (
    <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-4" noValidate>
      <Input label="Full name" autoComplete="off" error={errors.fullName?.message} {...register('fullName')} />
      <Input
        label="Phone (recommended)"
        type="tel"
        inputMode="tel"
        autoComplete="off"
        hint="Used to avoid adding the same person twice"
        error={errors.phone?.message}
        {...register('phone')}
      />
      <Input
        label="Email (optional)"
        type="email"
        inputMode="email"
        autoComplete="off"
        error={errors.email?.message}
        {...register('email')}
      />
      <Input
        label="Joined on"
        type="date"
        max={lusakaToday()}
        error={errors.joinedOn?.message}
        {...register('joinedOn')}
      />
      {add.isError && (
        <p role="alert" className="text-body text-bad">
          {errText(add.error)}
        </p>
      )}
      <Button type="submit" size="lg" isLoading={add.isPending}>
        {add.isPending ? 'Saving...' : 'Add member'}
      </Button>
    </form>
  )
}

export function MembersPage({ deptId }: { deptId: string }) {
  const profile = useProfile()
  const canWrite = canWriteDepartment(profile, deptId)
  const [search, setSearch] = useState('')
  const [showLeft, setShowLeft] = useState(false)
  const [sheetOpen, setSheetOpen] = useState(false)
  const toast = useToast()

  const members = useQuery({ queryKey: ['members', deptId], queryFn: () => listDepartmentMembers(deptId) })

  if (members.isPending) {
    return (
      <SkeletonRows rows={4} />
    )
  }
  if (members.isError) {
    return (
      <Card>
        <CardBody>
          <p role="alert" className="text-body text-bad">
            {errText(members.error)}
          </p>
          <Button variant="secondary" size="sm" className="mt-3" onClick={() => void members.refetch()}>
            Try again
          </Button>
        </CardBody>
      </Card>
    )
  }

  const all = members.data
  const current = all.filter((m) => m.leftOn === null)
  const pool = showLeft ? all : current
  const q = search.trim().toLowerCase()
  const qDigits = q.replace(/\D/g, '')
  const shown = pool.filter((m) => {
    if (!q) return true
    if (m.fullName.toLowerCase().includes(q)) return true
    const phone = (m.phone ?? '').replace(/\D/g, '')
    return qDigits !== '' && (phone.includes(qDigits) || phone.replace(/^260/, '0').includes(qDigits))
  })

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-title font-semibold text-ink">Members</h2>
          <p className="mt-1 text-muted">{current.length === 1 ? '1 member' : `${current.length} members`}</p>
        </div>
        {canWrite && all.length > 0 && (
          <Button size="lg" onClick={() => setSheetOpen(true)}>
            Add member
          </Button>
        )}
      </div>
      {canWrite && (
        <Link to="/d/$deptId/members/import" params={{ deptId }} className="-mt-2 inline-flex min-h-[44px] w-fit items-center rounded-control px-3 text-body font-medium text-brand hover:bg-neutral-tint">
          Import from a spreadsheet
        </Link>
      )}


      {all.length === 0 ? (
        <Card>
          <CardBody>
            <EmptyState
              title="No members yet"
              description={
                canWrite
                  ? 'Add the people in your department to start tracking their dues.'
                  : 'Nobody has been added to this department yet.'
              }
              action={
                canWrite ? (
                  <Button size="lg" onClick={() => setSheetOpen(true)}>
                    Add the first member
                  </Button>
                ) : undefined
              }
            />
          </CardBody>
        </Card>
      ) : (
        <>
          <Input
            label="Search"
            type="search"
            placeholder="Name or phone number"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoComplete="off"
          />
          <label className="flex min-h-[44px] items-center gap-3 text-body text-neutral">
            <input
              type="checkbox"
              className="h-5 w-5"
              checked={showLeft}
              onChange={(e) => setShowLeft(e.target.checked)}
            />
            Show people who have left
          </label>

          <Card>
            {shown.length === 0 ? (
              <CardBody>
                <p className="text-body text-muted">No one matches that. Try a different name or number.</p>
              </CardBody>
            ) : (
              <ul className="divide-y divide-line">
                {shown.map((m) => (
                  <li key={m.membershipId}>
                    <Link
                      to="/d/$deptId/members/$memberId"
                      params={{ deptId, memberId: m.memberId }}
                      className="flex min-h-[56px] items-center justify-between gap-3 px-4 py-3 hover:bg-page"
                    >
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-ink">{m.fullName}</span>
                        <span className="block text-body text-muted">
                          {m.phone ? formatZambianPhone(m.phone) : 'No phone number'}
                          {m.leftOn !== null && ' · Has left'}
                        </span>
                      </span>
                      {m.arrearsNgwee > 0 && (
                        <Badge tone="warning" className="shrink-0">
                          {formatKwacha(m.arrearsNgwee)} behind
                        </Badge>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </>
      )}

      {canWrite && (
        <Sheet isOpen={sheetOpen} onClose={() => setSheetOpen(false)} title="Add member">
          <AddMemberForm
            deptId={deptId}
            onDone={(message) => {
              toast.success(message)
              setSheetOpen(false)
            }}
          />
        </Sheet>
      )}
    </div>
  )
}
